
export interface WifiInfo {
  bssid: string;
  deviceName: string;
  deviceType: number;
  interval: number;
  ip: string;
  localIp: string;
  passwd: string;
  pinCode: string;
  port: number;
  rssi: number;
  ssid: string;
}

export interface StartRecordConfig {
  syncPlaying?: boolean
}

export interface Response<T = unknown> {
  code: number;
  data: T;
  msg?: string;
}

export interface NotifyEvent<T = unknown> {
  api: string;
  data: T;
}

export interface PcmFormat {
  bit_depth: number;
  number_of_channels: number;
  samples_per_packet: number;
  sample_rate: number;
}

export interface RecordStatus {
  status: "stop" | "error" | "started";
  pcmFormat?: PcmFormat;
}

export interface ConnectStatus {
  status: "error" | "disconnected";
}

export interface ScanDeviceData {
  length: number;
  errCode: number;
  [key: number]: WifiInfo;
}

export interface MicrophoneInfo {
  id: string;
  desc: string;
  isDefault: boolean;
}

export interface StartAudioMixRecordConfig {
  // 不需要
  // speakerInfo?: SpeakerInfo;
  micInfo: MicrophoneInfo;
}

export interface AddresssInfo {
  ip: string;
  port: number;
}

const fetch = async <T = unknown>(
  url: string,
  options?: Record<string, any>
): Promise<T> => {
  try {
    const res = await window.LinkService.request<Response<T>>(url, options);
    return res.data as T;
  } catch (error) {
    return Promise.reject(error);
  }
};

const subscribe = <T = unknown>(
  url: string,
  cb: (data: NotifyEvent<T>) => void
): (() => void) => {
  return window.LinkService.subscribe<NotifyEvent<T>>(url, cb);
};

const ScreenShareApis = {
  scanDevices() {
    return fetch<{ code: number; data: ScanDeviceData }>(
      "/api/process/v1/screenShare/scan"
    );
  },

  connectByPinCode(pinCode: string) {
    return fetch<Response>(
      "/api/process/v1/screenShare/connectByCode",
      { pinCode }
    );
  },

  connectByLocal(info: WifiInfo) {
    return fetch<{ code: number; data: number }>(
      "/api/process/v1/screenShare/connectByLocal",
      info
    );
  },

  switchNetwork(info: WifiInfo) {
    return fetch<Response>("/api/process/v1/screenShare/switchNetwork", info);
  },

  connectByHotspot(info: WifiInfo) {
    return fetch<Response>(
      "/api/process/v1/screenShare/connectByHotspot",
      info
    );
  },

  getCurrDeviceName() {
    return fetch<Response>(
      "/api/process/v1/screenShare/getRemoteDeviceName"
    );
  },

  disconnect(info: WifiInfo) {
    return fetch("/api/process/v1/screenShare/disconnect", info);
  },

  startRecord(config?: StartRecordConfig) {
    return fetch<{ code: number; data: boolean; msg: string }>(
      "/api/process/v1/screenShare/startRecord", config
    );
  },

  stopRecord() {
    return fetch("/api/process/v1/screenShare/stopRecord");
  },

  onConnectStatus(cb: (data: NotifyEvent<ConnectStatus>) => void) {
    return subscribe("/sub/process/v1/screenShare/connectStatus", cb);
  },

  onRecordStatus(cb: (data: NotifyEvent<RecordStatus>) => void) {
    return subscribe<RecordStatus>(
      "/sub/process/v1/screenShare/recordStatus",
      cb
    );
  },

  onRecordData(cb: (data: { data: number[] }) => void) {
    return subscribe("/sub/process/v1/screenShare/recordData", cb);
  },

  // 获取麦克风列表
  getAvailableMicrophones() {
    return fetch<Response<MicrophoneInfo[]>>(
      "/api/process/v1/screenShare/getAvailableMicrophones"
    );
  },

  // 开始录制，切换的话暂停上一个，传入新的麦克风就行
  startLocalRecord(config?: StartAudioMixRecordConfig) {
    return fetch<Response>("/api/process/v1/screenShare/startLocalRecord", config);
  },

  stopLocalRecord() {
    return fetch("/api/process/v1/screenShare/stopLocalRecord");
  },

  onLocalRecordStatus(cb: (data: NotifyEvent<RecordStatus>) => void) {
    return subscribe<RecordStatus>(
      "/sub/process/v1/screenShare/localRecordStatus",
      cb
    );
  },

  onLocalRecordData(cb: (data: { data: number[] }) => void) {
    return subscribe("/sub/process/v1/screenShare/localRecordData", cb);
  },

  // 获取麦克风列表
  getRemoteAddresss() {
    return fetch<Response<AddresssInfo>>(
      "/api/process/v1/screenShare/getRemoteAddresss"
    );
  },
};

export default ScreenShareApis;
